import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarDays, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Editable Events URL + force-refresh card.
 *
 * Props:
 * - institute: { events_url, ... }
 * - saveEndpoint: e.g. `/institutes/{id}` (super_admin) or `/institutes/me` (institute_admin)
 * - refreshEndpoint: e.g. `/institutes/{id}/events/refresh` (super_admin) or `/agenda/events/refresh` (institute_admin)
 * - onSaved(updatedInstitute): callback after a successful save
 */
export default function EventsUrlPanel({ institute, saveEndpoint, refreshEndpoint, onSaved }) {
  const [url, setUrl] = useState(institute?.events_url || "");
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const dirty = (url || "") !== (institute?.events_url || "");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(saveEndpoint, { events_url: url });
      toast.success("Events URL saved");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    setLastResult(null);
    try {
      const { data } = await api.post(refreshEndpoint);
      setLastResult({
        count: data.events?.length || 0,
        fetched_at: data.fetched_at,
        error: data.error,
        stale: data.stale,
      });
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Loaded ${data.events?.length || 0} events`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div
      className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8"
      data-testid="events-url-panel"
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-xl bg-[#F2F0EA] flex items-center justify-center">
          <CalendarDays strokeWidth={1.5} className="text-[#B37A5F]" size={18} />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188]">
            Upcoming Events
          </p>
          <h3 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold">
            Events source URL
          </h3>
        </div>
      </div>
      <p className="text-sm text-[#5C635A] mb-5 leading-relaxed max-w-2xl">
        The Upcoming Events page polls this URL once a month to refresh the cached events. You can
        also force a refresh now using the button below.
      </p>

      <div className="space-y-2 max-w-2xl mb-4">
        <Label htmlFor="events-url-input" className="text-xs uppercase tracking-[0.2em] text-[#1A1D1A] font-bold">
          URL
        </Label>
        <Input
          id="events-url-input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://your-institute.example/agenda"
          data-testid="events-url-input"
          className="h-11 rounded-lg border-[#E5E2DC]"
          type="url"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="events-url-save-button"
          className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
        >
          <Check strokeWidth={1.5} size={16} className="mr-2" />
          {saving ? "Saving…" : "Save URL"}
        </Button>
        <Button
          onClick={refresh}
          disabled={refreshing || !institute?.events_url}
          data-testid="events-url-refresh-button"
          variant="outline"
          className="h-11 rounded-lg border-[#E5E2DC] text-[#1A1D1A] hover:bg-[#F2F0EA]"
        >
          <RefreshCw strokeWidth={1.5} size={16} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Force refresh now"}
        </Button>
        {!institute?.events_url && (
          <p className="text-xs text-[#8A9188] uppercase tracking-[0.2em] font-bold">
            Save a URL first to enable refresh
          </p>
        )}
      </div>

      {lastResult && (
        <p
          className="text-xs uppercase tracking-[0.2em] font-bold mt-4"
          data-testid="events-url-last-result"
          style={{ color: lastResult.error ? "#9C6A53" : "#4A5D4E" }}
        >
          {lastResult.error
            ? `Refresh failed · ${lastResult.error}`
            : `Loaded ${lastResult.count} events at ${new Date(lastResult.fetched_at).toLocaleString()}`}
        </p>
      )}
    </div>
  );
}
